import _ from "lodash";

import { ALL_MODEL_INDEXES } from "@/constants";

import BaseApi from "../../api/BaseApi";
import AppModel from "../AppModel";
import { loadFileAsString, loadUrl } from "@/utils/my-file-util";

import localforage from "localforage";
import InMemoryStorage from "../storage/InMemoryStorage";

const CDN_HOST_URL = process.env.VUE_APP_CDN_HOST_URL || "";

// modelUrl은 CDN 버킷 키. 예) "models/v5/tiger_v5+240415_135606.glb", "models/v4/dog_v4/dog_v4.gltf"
const parseModelUrl = (modelUrl) => {
  const fileUrl = `${CDN_HOST_URL}/${modelUrl}`;
  const fileExtension = (modelUrl.split(".").pop() || "glb").toLowerCase();
  // gltf(v4)는 외부 bin/텍스처를 상대경로로 참조 → load()로 gltf URL 기준 해석 필요
  const isGltf = fileExtension === "gltf";

  return {
    fileUrl,
    fileExtension,
    isGltf,
    cacheKey: modelUrl, // 캐시는 키 전체로(고유)
  };
};

// AppModel이란
// Three.js에서 표현할 수 있는 파일
// 이걸 ModelFacade에 바이너리 형태로 넘겨준다

let _store = null;

class AppModelFacade {
  // TODO: 다른쪽으로 옮기기
  static async fetchRandomModel() {
    const modelIndex = _.sample(ALL_MODEL_INDEXES); // get random index;
    return await this.fetchModel(`models/v4/${modelIndex}_v4/${modelIndex}_v4.gltf`);
  }

  /**
   * @param {string} modelUrl - CDN 버킷 키 (예: "models/v5/xxx.glb")
   * @returns	{AppModel}
   */
  static async fetchModel(modelUrl) {
    if (!modelUrl) throw new Error("MODEL_URL_IS_REQUIRED");

    const { fileUrl, isGltf, cacheKey } = parseModelUrl(modelUrl);

    // gltf(v4): 외부 bin/텍스처를 gltf URL 기준으로 해석해야 하므로 load() 사용 (캐시 미적용)
    if (isGltf) {
      return await AppModel.load(fileUrl);
    }

    // glb: 자기완결 바이너리 → arraybuffer 캐시 후 parse
    if (_store === null) {
      _store = localforage.createInstance({ name: "virtupet" });
    }

    const isExist = (await _store.keys()).includes(cacheKey);
    if (isExist) {
      const arrayBuffer = await _store.getItem(cacheKey);
      return await AppModel.parse(arrayBuffer);
    } else {
      const arrayBuffer = await BaseApi.fetchModelArrayBuffer(fileUrl);
      await _store.setItem(cacheKey, arrayBuffer);
      return await AppModel.parse(arrayBuffer);
    }

    const appModel = await AppModel.load(fileUrl);
    return appModel;
  }

  static async loadModelFrom(files) {
    // gltf는 여러개의 파일로 이루어져있다

    const extensionNames = Object.values(files)
      .map((file) => file.name)
      .map((name) => name.split(".")[1]);

    if (extensionNames.includes("gltf")) {
      // { "fileName1" : url, "fileName2" }
      const pngFileMap = Object.values(files).reduce((acc, file) => {
        if (file.name.endsWith(".png")) {
          // remove extension name
          const name = file.name.split(".")[0];
          acc[name] = loadUrl(file);
        }
        return acc;
      }, {});

      const binFileUrl = loadUrl(Object.values(files).find((file) => file.name.endsWith(".bin")));

      const gltfFile = Object.values(files).find((file) => file.name.endsWith(".gltf"));
      const str = await loadFileAsString(gltfFile);
      const json = JSON.parse(str);

      json.images = json.images.map((image) => {
        return {
          ...image,
          uri: pngFileMap[image.name],
        };
      });

      json.buffers[0].uri = binFileUrl;

      const appModel = await AppModel.parse(JSON.stringify(json));
      return appModel;

      // const resultJson = JSON.stringify(json);
      // const blob = new Blob([resultJson], { type: "application/json" });
      // const url = loadUrl(blob);
      // return url;
    } else if (extensionNames.includes("glb")) {
      const file = Object.values(files).find((file) => file.name.endsWith(".glb"));
      const url = loadUrl(file);
      const appModel = await AppModel.load(url);
      return appModel;
    } else {
      throw new Error("E_ONLY_GLB_OR_GLTF");
    }
  }
}

export default AppModelFacade;
